import axios from 'axios';
import { API_URL } from 'env';
import {
  ApiDayItem,
  ApiDayQuestionsResponse,
  ApiDayResultResponse,
  ApiQuestion,
  ApiResultQuestion,
  DayMeta,
  DayStatus,
  OptionKey,
  Question,
  QuestionOption,
  AssessmentDashboardResponse,
  AssessmentDashboardParams,
} from '../data/types';

// Base URL cho Assessment API theo cấu hình
export const API_V1_BE_LOCAL = 'https://ftmo-api-dev.buso.asia/api/v1/be';

/**
 * Get configured Base URL for Assessment endpoints
 * Mặc định: https://ftmo-api-dev.buso.asia/api/v1/be
 * Tự động chuẩn hóa & bổ sung tiền tố http/https và /api/v1/be nếu bị thiếu
 */
export const getAssessmentApiBaseUrl = (): string => {
  const localOverride = localStorage.getItem('api_v1_be_local');
  if (localOverride) {
    let url = localOverride.trim().replace(/\/+$/, '');
    // Bổ sung tiền tố protocol nếu thiếu
    if (!url.startsWith('http://') && !url.startsWith('https://')) {
      url = `https://${url}`;
    }
    // Bổ sung tiền tố đường dẫn /api/v1/be nếu thiếu
    if (!url.includes('/api/v1/be')) {
      url = `${url}/api/v1/be`;
    }
    return url;
  }

  // Tiền tố chuẩn: https://ftmo-api-dev.buso.asia/api/v1/be
  if (API_URL) {
    const trimmed = API_URL.trim().replace(/\/+$/, '');
    const withProto = !trimmed.startsWith('http://') && !trimmed.startsWith('https://')
      ? `https://${trimmed}`
      : trimmed;
    return withProto.includes('/api/v1/be') ? withProto : `${withProto}/api/v1/be`;
  }

  return API_V1_BE_LOCAL;
};


export const getAssessmentAuthToken = (): string => {
  // Only the signed-in user's own token: never fall back to a token baked into the bundle,
  // which would make a signed-out visitor read and answer as that other account
  return localStorage.getItem('token') || localStorage.getItem('accessToken') || '';
};

export const assessmentApiClient = axios.create({
  timeout: 15000,
  headers: {
    'Content-Type': 'application/json',
    Accept: 'application/json',
  },
});

assessmentApiClient.interceptors.request.use((config) => {
  config.baseURL = getAssessmentApiBaseUrl();
  const token = getAssessmentAuthToken();
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Mapping option keys for question answers A -> E
const OPTION_KEYS: OptionKey[] = ['A', 'B', 'C', 'D', 'E'];

/**
 * Normalizes backend status string to DayStatus ('AVAILABLE' | 'IN_PROGRESS' | 'COMPLETED' | 'LOCKED')
 */
export function mapApiDayStatusToDayStatus(apiStatus?: string): DayStatus {
  if (!apiStatus) return 'AVAILABLE';
  const s = apiStatus.trim().toLowerCase();
  if (s === 'completed' || s === 'da_hoan_thanh') return 'COMPLETED';
  if (s === 'in progress' || s === 'in_progress' || s === 'dang_lam') return 'IN_PROGRESS';
  if (s === 'locked' || s === 'khoa') return 'LOCKED';
  return 'AVAILABLE';
}

/**
 * Maps ApiDayItem[] from GET /assessment/days into DayMeta[]
 */
export function mapApiDaysToDayMeta(apiDays: ApiDayItem[]): DayMeta[] {
  return apiDays.map((d) => {
    const dayNum = d.day_number || d.id;
    return {
      day: dayNum,
      title: d.title.replace(/^Ngày\s*\d+\s*[-–—:]\s*/i, '').trim() || d.title,
      group: d.title,
      totalQuestions: d.total_questions || 34,
      count: d.total_questions || 34,
      status: mapApiDayStatusToDayStatus(d.status),
      progress: d.progress,
    };
  });
}

/**
 * Maps ApiQuestion[] from GET /assessment/days/{dayId}/questions into frontend Question[]
 */
export function mapApiQuestionsToQuestions(apiQuestions: ApiQuestion[]): Question[] {
  return apiQuestions.map((q) => {
    const options: QuestionOption[] = (q.answers || []).map((ans, idx) => ({
      id: ans.id,
      key: OPTION_KEYS[idx] || 'E',
      text: ans.content,
      score: ans.score,
    }));

    return {
      id: q.id,
      day: q.assessment_day_id,
      competency: q.competency_key || 'Chung',
      group: `Ngày ${q.assessment_day_id}`,
      text: q.content,
      options,
      maxScore: Math.max(...options.map((o) => o.score || 0), 5),
    };
  });
}

/**
 * Parses answered payload from API into questionId -> OptionKey map
 */
export function parseAnsweredToOptionMap(
  answered: any,
  questions: Question[]
): Record<number, OptionKey> {
  const result: Record<number, OptionKey> = {};
  if (!answered) return result;

  // Build lookup: questionId -> answerId -> OptionKey
  const qLookup = new Map<number, Map<number, OptionKey>>();
  questions.forEach((q) => {
    const aMap = new Map<number, OptionKey>();
    q.options.forEach((opt) => {
      if (opt.id !== undefined) {
        aMap.set(opt.id, opt.key);
      }
    });
    qLookup.set(q.id, aMap);
  });

  if (Array.isArray(answered)) {
    answered.forEach((item: any) => {
      if (typeof item === 'object' && item !== null) {
        const qId = item.question_id || item.questionId;
        const aId = item.answer_id || item.answerId || item.id;
        if (qId && aId) {
          const aMap = qLookup.get(Number(qId));
          const optKey = aMap?.get(Number(aId));
          if (optKey) {
            result[Number(qId)] = optKey;
          }
        }
      } else if (typeof item === 'number') {
        // Maybe item is an answer_id: find question containing this answer
        for (const q of questions) {
          const matched = q.options.find((opt) => opt.id === item);
          if (matched) {
            result[q.id] = matched.key;
            break;
          }
        }
      }
    });
  } else if (typeof answered === 'object') {
    Object.entries(answered).forEach(([qKey, aVal]: [string, any]) => {
      const qId = Number(qKey);
      const aId = typeof aVal === 'object' && aVal !== null ? aVal.answer_id || aVal.id : Number(aVal);
      if (qId && aId) {
        const aMap = qLookup.get(qId);
        const optKey = aMap?.get(aId);
        if (optKey) {
          result[qId] = optKey;
        }
      }
    });
  }

  return result;
}

/**
 * Maps ApiResultQuestion[] from GET /assessment/days/{dayId}/result into frontend Question[]
 */
export function mapApiResultQuestionsToQuestions(
  resultQuestions: ApiResultQuestion[],
  dayId: number
): Question[] {
  return resultQuestions.map((q) => {
    const options: QuestionOption[] = (q.answers || []).map((ans, idx) => ({
      id: ans.id,
      key: OPTION_KEYS[idx] || 'E',
      text: ans.content,
      score: ans.score,
    }));

    return {
      id: q.question_id,
      day: Number(dayId),
      competency: q.competency_key || 'Chung',
      group: `Ngày ${dayId}`,
      text: q.content,
      options,
      maxScore: q.max_score || Math.max(...options.map((o) => o.score || 0), 5),
      earnedScore: q.earned_score !== undefined && q.earned_score !== null ? Number(q.earned_score) : undefined,
    };
  });
}

/**
 * Parses user selected answers from ApiResultQuestion[] into questionId -> OptionKey map
 */
export function parseResultAnswerMap(
  resultQuestions: ApiResultQuestion[],
  questions?: Question[]
): Record<number, OptionKey> {
  const result: Record<number, OptionKey> = {};
  if (!Array.isArray(resultQuestions)) return result;

  const qMap = new Map<number, Question>();
  if (Array.isArray(questions)) {
    questions.forEach((q) => qMap.set(Number(q.id), q));
  }

  resultQuestions.forEach((rq) => {
    const qId = Number(rq.question_id);
    if (!qId) return;

    const hasSelectedId =
      rq.user_selected_answer_id !== undefined &&
      rq.user_selected_answer_id !== null &&
      Number(rq.user_selected_answer_id) > 0;
    const targetAnswerId = hasSelectedId ? Number(rq.user_selected_answer_id) : null;

    // 1. Kiểm tra trực tiếp rq.answers theo thứ tự index (0->A, 1->B, 2->C, 3->D, 4->E)
    if (Array.isArray(rq.answers)) {
      const selectedIndex = rq.answers.findIndex((ans) => {
        if (
          ans.is_selected === true ||
          (ans as any).is_selected === 1 ||
          String(ans.is_selected).toLowerCase() === 'true'
        ) {
          return true;
        }
        if (targetAnswerId !== null && Number(ans.id) === targetAnswerId) {
          return true;
        }
        return false;
      });

      if (selectedIndex >= 0 && OPTION_KEYS[selectedIndex]) {
        result[qId] = OPTION_KEYS[selectedIndex] as OptionKey;
        return;
      }
    }

    // 2. Fallback: Nếu câu hỏi có trong qMap
    const q = qMap.get(qId);
    if (q) {
      let selectedAnswerId: number | null = targetAnswerId;
      if (selectedAnswerId === null && Array.isArray(rq.answers)) {
        const selectedAns = rq.answers.find(
          (a) =>
            a.is_selected === true ||
            (a as any).is_selected === 1 ||
            String(a.is_selected).toLowerCase() === 'true'
        );
        if (selectedAns && Number(selectedAns.id) > 0) {
          selectedAnswerId = Number(selectedAns.id);
        }
      }

      if (selectedAnswerId !== null && selectedAnswerId > 0) {
        const matchedOpt = q.options.find((opt) => Number(opt.id) === selectedAnswerId);
        if (matchedOpt) {
          result[qId] = matchedOpt.key;
        }
      }
    }
  });

  return result;
}



export const assessmentApi = {
  /**
   * Load dữ liệu các ngày
   * GET /assessment/days
   */
  async getAssessmentDays(): Promise<ApiDayItem[]> {
    const res = await assessmentApiClient.get<any>('/assessment/days');
    if (Array.isArray(res.data)) return res.data;
    if (res.data && Array.isArray(res.data.data)) return res.data.data;
    if (res.data && Array.isArray(res.data.days)) return res.data.days;
    return res.data || [];
  },

  /**
   * Load lượng câu hỏi 1 ngày
   * GET /assessment/days/{dayId}/questions
   */
  async getDayQuestions(dayId: number): Promise<ApiDayQuestionsResponse> {
    const res = await assessmentApiClient.get<ApiDayQuestionsResponse>(
      `/assessment/days/${dayId}/questions`
    );
    return res.data;
  },

  /**
   * Load lại kết quả & đáp án đã nộp
   * GET /assessment/days/{dayId}/result
   */
  async getDayResult(dayId: number): Promise<ApiDayResultResponse> {
    const res = await assessmentApiClient.get<ApiDayResultResponse>(
      `/assessment/days/${dayId}/result`
    );
    return res.data;
  },

  /**
   * Trả lời 1 câu hỏi
   * POST /assessment/submit-answer
   * Body: { day_id, question_id, answer_id }
   */
  async submitAnswer(
    dayId: number,
    questionId: number,
    answerId: number
  ): Promise<{ success: boolean; message: string }> {
    const res = await assessmentApiClient.post<{ success: boolean; message: string }>(
      '/assessment/submit-answer',
      {
        day_id: dayId,
        question_id: questionId,
        answer_id: answerId,
      }
    );
    return res.data;
  },

  /**
   * Nộp đáp án 1 ngày
   * POST /assessment/days/{dayId}/submit
   */
  async submitDay(dayId: number): Promise<{ success: boolean; message: string }> {
    const res = await assessmentApiClient.post<{ success: boolean; message: string }>(
      `/assessment/days/${dayId}/submit`
    );
    return res.data;
  },

  /**
   * Tải dữ liệu Dashboard cho Admin / Mentor
   * GET /assessment/dashboard
   * Params: search, classification, page
   */
  async getDashboard(params?: AssessmentDashboardParams): Promise<AssessmentDashboardResponse> {
    const queryParams: Record<string, any> = {};
    if (params?.search && params.search.trim()) {
      queryParams.search = params.search.trim();
    }
    if (params?.page) {
      queryParams.page = params.page;
    }
    if (params?.day_id !== undefined && params.day_id !== null && params.day_id !== '' && params.day_id !== 'ALL') {
      queryParams.day_id = params.day_id;
    }
    if (params?.classification) {
      if (Array.isArray(params.classification)) {
        if (params.classification.length > 0) {
          queryParams.classification = params.classification;
        }
      } else if (params.classification !== 'ALL') {
        queryParams.classification = params.classification;
      }
    }

    const res = await assessmentApiClient.get<any>(
      '/assessment/dashboard',
      {
        params: queryParams,
      }
    );
    if (res.data && res.data.stats && res.data.students) {
      return res.data;
    }
    if (res.data && res.data.data && res.data.data.stats && res.data.data.students) {
      return res.data.data;
    }
    return res.data;
  },
};

